import React, { useState, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { SELECTION_CHANGE_COMMAND, FORMAT_TEXT_COMMAND, $getSelection, $isRangeSelection } from 'lexical';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { BoldIcon, ItalicIcon, UnderlineIcon, LinkIcon } from 'lucide-react';

function Toolbar() {
  const [editor] = useLexicalComposerContext();
  const [url, setUrl] = useState('');
  const [isLinkInputVisible, setIsLinkInputVisible] = useState(false);
  const [activeFormats, setActiveFormats] = useState(new Set());

  useEffect(() => {
    const updateToolbar = () => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const formats = new Set();
        if (selection.hasFormat('bold')) formats.add('bold');
        if (selection.hasFormat('italic')) formats.add('italic');
        if (selection.hasFormat('underline')) formats.add('underline');
        setActiveFormats(formats);
      } else {
        setActiveFormats(new Set()); // Clear formats if no valid selection
      }
    };

    // Register the selection change command and clean up after unmount
    const unregisterCommand = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      0
    );

    return () => {
      unregisterCommand(); // Cleanup on component unmount
    };
  }, [editor]);

  const toggleFormat = (format) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const insertLink = () => {
    if (!url) return;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    setUrl('');
    setIsLinkInputVisible(false);
  };

  const removeLink = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
  };

  return (
    <div className="flex items-center space-x-2 p-2 bg-gray-800 border-b border-gray-700">
      <button
        onClick={() => toggleFormat('bold')}
        className={`p-2 rounded hover:bg-gray-700 ${
          activeFormats.has('bold') ? 'bg-gray-700' : 'bg-gray-900'
        }`}
        aria-label="Format Bold"
      >
        <BoldIcon className="w-5 h-5 text-white" />
      </button>

      <button
        onClick={() => toggleFormat('italic')}
        className={`p-2 rounded hover:bg-gray-700 ${
          activeFormats.has('italic') ? 'bg-gray-700' : 'bg-gray-900'
        }`}
        aria-label="Format Italic"
      >
        <ItalicIcon className="w-5 h-5 text-white" />
      </button>

      <button
        onClick={() => toggleFormat('underline')}
        className={`p-2 rounded hover:bg-gray-700 ${
          activeFormats.has('underline') ? 'bg-gray-700' : 'bg-gray-900'
        }`}
        aria-label="Format Underline"
      >
        <UnderlineIcon className="w-5 h-5 text-white" />
      </button>

      <div className="relative">
        <button
          onClick={() => setIsLinkInputVisible(!isLinkInputVisible)}
          className="p-2 bg-gray-900 rounded hover:bg-gray-700"
          aria-label="Insert Link"
        >
          <LinkIcon className="w-5 h-5 text-white" />
        </button>

        {isLinkInputVisible && (
          <div className="absolute left-0 mt-2 p-2 bg-gray-800 border border-gray-700 rounded shadow-lg z-10">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Enter URL"
              className="p-1 mb-2 w-64 bg-gray-900 border border-gray-700 rounded text-white"
            />
            <div className="flex space-x-2">
              <button
                onClick={insertLink}
                className="p-1 bg-blue-600 rounded hover:bg-blue-700 text-white text-sm"
              >
                Add Link
              </button>
              <button
                onClick={removeLink}
                className="p-1 bg-gray-700 rounded hover:bg-gray-600 text-white text-sm"
              >
                Remove Link
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Toolbar;
